/**
 * Session monitor hook — proactive JWT expiry detection.
 *
 * Mount once in ProtectedRoute to get:
 * 1. Automatic logout when the JWT `exp` claim is reached
 * 2. Warning countdown 5 minutes before expiry
 * 3. Immediate token check when a hidden tab becomes visible
 * 4. Cross-tab logout synchronisation via BroadcastChannel
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { getTokenTTL, isTokenExpired } from '@/lib/token'

/** Time before expiry to show warning (5 minutes). */
const EXPIRY_WARNING_MS = 5 * 60 * 1000

/** BroadcastChannel name for cross-tab logout sync. */
const LOGOUT_CHANNEL = 'uai-session-logout'

export interface UseSessionMonitorReturn {
  /** True when token is about to expire and user should be warned. */
  showExpiryWarning: boolean
  /** Milliseconds until token expires (null if no token). */
  timeUntilExpiry: number | null
  /** Dismiss the warning (user acknowledged). */
  dismissWarning: () => void
}

export function useSessionMonitor(): UseSessionMonitorReturn {
  const token = useAuth((s) => s.token)
  const logoutWithReason = useAuth((s) => s.logoutWithReason)

  const [showExpiryWarning, setShowExpiryWarning] = useState(false)
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null)

  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const channelRef = useRef<BroadcastChannel | null>(null)

  const clearTimers = useCallback(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current)
    expiryTimerRef.current = null
    warningTimerRef.current = null
    countdownIntervalRef.current = null
  }, [])

  const performLogout = useCallback(() => {
    clearTimers()
    setShowExpiryWarning(false)
    logoutWithReason('token_expired')
    // Broadcast to other tabs
    channelRef.current?.postMessage('logout')
  }, [clearTimers, logoutWithReason])

  const dismissWarning = useCallback(() => {
    setShowExpiryWarning(false)
  }, [])

  // ── Set up timers when token changes ────────────────────────────
  useEffect(() => {
    if (!token) {
      clearTimers()
      setTimeUntilExpiry(null)
      setShowExpiryWarning(false)
      return
    }

    // Already expired?
    if (isTokenExpired(token)) {
      performLogout()
      return
    }

    const ttl = getTokenTTL(token)
    if (ttl === null) return

    setTimeUntilExpiry(ttl)

    // Warning timer — fires EXPIRY_WARNING_MS before expiry
    if (ttl > EXPIRY_WARNING_MS) {
      warningTimerRef.current = setTimeout(() => {
        setShowExpiryWarning(true)
        // Start 1-second countdown updates
        countdownIntervalRef.current = setInterval(() => {
          const remaining = getTokenTTL(token)
          setTimeUntilExpiry(remaining)
          if (remaining !== null && remaining <= 0) {
            performLogout()
          }
        }, 1000)
      }, ttl - EXPIRY_WARNING_MS)
    } else {
      // Already within warning window
      setShowExpiryWarning(true)
    }

    // Hard expiry timer
    expiryTimerRef.current = setTimeout(performLogout, ttl)

    return clearTimers
  }, [token, clearTimers, performLogout])

  // ── Page Visibility API — re-check token when tab becomes visible ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token) {
        if (isTokenExpired(token)) {
          performLogout()
        } else {
          // Recalculate TTL (timers may have drifted while tab was hidden)
          const ttl = getTokenTTL(token)
          setTimeUntilExpiry(ttl)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [token, performLogout])

  // ── BroadcastChannel — sync logout across tabs ──────────────────
  useEffect(() => {
    try {
      channelRef.current = new BroadcastChannel(LOGOUT_CHANNEL)
      channelRef.current.onmessage = (event) => {
        if (event.data === 'logout') {
          clearTimers()
          setShowExpiryWarning(false)
          logoutWithReason('token_expired')
        }
      }
    } catch {
      // BroadcastChannel not supported — degrade gracefully
    }

    return () => {
      channelRef.current?.close()
      channelRef.current = null
    }
  }, [clearTimers, logoutWithReason])

  return { showExpiryWarning, timeUntilExpiry, dismissWarning }
}
